import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "aos/dist/aos.css";
import {
  CheckCircle,
  BookOpen,
  Code2,
  FileText,
  Layout,
  Monitor,
  Layers,
  Table,
  Film,
  Globe,
  ClipboardList,
  UserCircle,
  ShoppingCart,
  Star,
  Rocket,
} from "lucide-react";
import { Link } from "react-router-dom";

const HtmlCourse = () => {
  return (
    <div className="bg-light text-dark overflow-hidden">
      {/* Hero Section */}
      <section
        className="text-white py-24" // increased vertical padding for taller hero
        style={{
          background:
            "linear-gradient(rgba(220,53,69,0.9), rgba(0,0,0,0.8)), url('/assets/pngtree-closeup-photograph-of-a-notebook-showing-the-application-of-css-javascript-and-html-in-the-function-source-code-as-captured-on-the-monitor-photo-image_40975884.jpg') center/cover no-repeat",
        }}
      >
        <div className="container text-center" data-aos="zoom-in">
          <h1 className="fw-bold display-4 mt-5 animate__animated animate__bounce">
            📄 HTML Full Course
          </h1>
          <p className="lead mt-3 text-lg md:text-xl animate__animated animate__fadeInUp">
            Learn the foundation of web development with in-depth HTML concepts
            and real-world examples.
          </p>
          <Link
            href="/contact"
            className="btn btn-light fw-bold px-4 py-2 shadow animate-bounce mt-5" // smaller button
          >
            🚀 Enroll Now
          </Link>
        </div>
      </section>

      {/* Overview */}
      <section className="container my-5">
        <h2
          className="fw-bold text-center text-black mb-5 display-5"
          data-aos="fade-up"
        >
          Course Overview
        </h2>
        <div className="row g-4 align-items-center">
          <div className="col-md-6" data-aos="fade-right">
            <img
              src="/assets/programming-background-collage_23-2149901776.jpg"
              alt="HTML Overview"
              className="img-fluid rounded-4 shadow"
            />
          </div>
          <div className="col-md-6" data-aos="fade-left">
            <ul className="list-unstyled fs-5 mb-0 card p-4 shadow border-0 bg-white text-lg md:text-xl">
              <li className="mb-2 d-flex align-items-center">
                <FileText className="text-danger me-2 animate-bounce" /> HTML
                Document Structure
              </li>
              <li className="mb-2 d-flex align-items-center">
                <Layout className="text-primary me-2 animate-pulse" /> Headings,
                Paragraphs, and Lists
              </li>
              <li className="mb-2 d-flex align-items-center">
                <Table className="text-success me-2 animate-spin" /> Links,
                Images & Tables
              </li>
              <li className="mb-2 d-flex align-items-center">
                <ClipboardList className="text-warning me-2 animate-shake" />{" "}
                Forms, Inputs, and Media Tags
              </li>
              <li className="mb-2 d-flex align-items-center">
                <Layers className="text-info me-2 animate-bounce" /> Semantic
                Elements & HTML5 Features
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="bg-white py-5">
        <div className="container">
          <h2
            className="fw-bold text-center text-black mb-5 display-5"
            data-aos="fade-up"
          >
            Topics Covered
          </h2>
          <div className="row g-4 text-center">
            {[
              {
                name: "HTML Tags",
                icon: <Code2 className="animate-bounce" />,
                color: "bg-danger text-white",
              },
              {
                name: "Elements & Attributes",
                icon: <Layers className="animate-pulse" />,
                color: "bg-primary text-white",
              },
              {
                name: "Forms & Controls",
                icon: <ClipboardList className="animate-bounce" />,
                color: "bg-success text-white",
              },
              {
                name: "Multimedia",
                icon: <Film className="animate-spin" />,
                color: "bg-warning text-dark",
              },
              {
                name: "Entities",
                icon: <Star className="animate-pulse" />,
                color: "bg-info text-dark",
              },
              {
                name: "Tables",
                icon: <Table className="animate-bounce" />,
                color: "bg-secondary text-white",
              },
              {
                name: "iFrames",
                icon: <Monitor className="animate-shake" />,
                color: "bg-dark text-white",
              },
              {
                name: "HTML5 APIs",
                icon: <Globe className="animate-spin" />,
                color: "bg-teal text-white",
              },
            ].map((topic, index) => (
              <div
                key={index}
                className="col-6 col-md-3"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <div
                  className={`p-4 rounded shadow-sm h-100 ${topic.color} d-flex flex-column align-items-center justify-content-center`}
                  style={{ transition: "transform 0.3s" }}
                >
                  <div className="mb-2 fs-3">{topic.icon}</div>
                  <div className="fw-semibold fs-5">{topic.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Practice Projects */}
      <section className="container my-5">
        <h2
          className="fw-bold text-center text-black mb-5 display-5"
          data-aos="fade-up"
        >
          Practice Projects
        </h2>
        <div className="row g-4">
          {[
            {
              title: "Simple Portfolio",
              desc: "Build your first personal page with bio, image, and contact details.",
              icon: (
                <UserCircle className="text-primary animate-pulse" size={32} />
              ),
              color: "bg-light border-primary",
            },
            {
              title: "Survey Form",
              desc: "Create a form with inputs, radio buttons, and submission using only HTML.",
              icon: (
                <ClipboardList
                  className="text-success animate-bounce"
                  size={32}
                />
              ),
              color: "bg-light border-success",
            },
            {
              title: "Product Table",
              desc: "Design a product listing table with rows, columns, and basic styling.",
              icon: (
                <ShoppingCart className="text-danger animate-spin" size={32} />
              ),
              color: "bg-light border-danger",
            },
          ].map((project, index) => (
            <div key={index} className="col-md-4" data-aos="fade-up">
              <div className={`card shadow border-2 ${project.color}`}>
                <div className="card-body text-center d-flex flex-column align-items-center justify-content-center">
                  <div className="mb-3">{project.icon}</div>
                  <h5 className="card-title mt-2">{project.title}</h5>
                  <p className="card-text">{project.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-dark text-white py-5">
        <div className="container">
          <h2
            className="fw-bold text-center text-white mb-5 display-5"
            data-aos="fade-up"
          >
            Why Learn HTML?
          </h2>
          <div className="row g-4 text-center">
            {[
              {
                title: "Universal Skill",
                desc: "HTML is the backbone of every website and web application.",
                icon: <Globe className="text-info animate-spin" size={32} />,
                color: "bg-secondary",
              },
              {
                title: "Easy to Learn",
                desc: "Great for beginners and requires no prior programming knowledge.",
                icon: (
                  <BookOpen className="text-warning animate-bounce" size={32} />
                ),
                color: "bg-success",
              },
              {
                title: "Foundation for Web Dev",
                desc: "Essential before moving to CSS, JavaScript, React, or full-stack development.",
                icon: (
                  <Rocket className="text-danger animate-shake" size={32} />
                ),
                color: "bg-primary",
              },
            ].map((benefit, index) => (
              <div key={index} className="col-md-4" data-aos="fade-up">
                <div
                  className={`p-4 rounded shadow-sm h-100 d-flex flex-column align-items-center justify-content-center ${benefit.color}`}
                  style={{ transition: "transform 0.3s" }}
                >
                  <div className="mb-3">{benefit.icon}</div>
                  <h5 className="mt-2">{benefit.title}</h5>
                  <p>{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer Enroll Section */}
      <footer className="bg-red-600 text-white text-center py-5">
        <h3 className="fw-bold mb-3 animate__animated animate__heartBeat">
          Ready to Start Your Web Development Journey?
        </h3>
        <p className="mb-4">
          Learn HTML with hands-on examples and projects to kickstart your
          career in web development.
        </p>
        <Link
          to="/contact"
          className="btn btn-light fw-bold px-4 py-2 shadow animate-bounce"
        >
          🚀 Enroll Now
        </Link>
      </footer>
    </div>
  );
};

export default HtmlCourse;
